/**
 * RESPONSIBILITY: who you are, and where you can go, as one item in the nav.
 *
 * A strip across the top of the site was too invasive: it is chrome for the two people
 * who edit, sitting above the page for everyone. This is an icon in the bar the site
 * already has, and it opens the same panel the ministry menus use — so it costs a
 * visitor nothing and looks like part of the design rather than a tool bolted on.
 *
 * Every destination is also a URL. A menu that can be hidden must never be the only way
 * in, which is why these are links to screens and not a second implementation of them.
 */
import { ADMIN_ROLE } from '@framework/core';
import { useAuth, SCREENS, useFlag, setFlag, FLAG_ACCOUNT_BAR } from '@framework/react';
import { FLAG_THEME_PENCIL } from '../../flags';
import { canSeeRequests } from '@app/sections';
import { Page, hrefFor } from '../commons/pages';
import { useWords } from '../commons/words';

/** Initials, so a signed-in person is recognisable without a photograph. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function AccountMenu() {
  const { session, logout } = useAuth();
  const w = useWords();
  const themePencil = useFlag(FLAG_THEME_PENCIL);
  const accountBar = useFlag(FLAG_ACCOUNT_BAR);

  /*
   * SIGNED OUT, there is no visible way in — a church website does not advertise a staff
   * door to every visitor — UNLESS the account flag is on.
   *
   * That flag is documented as showing "Sign in / Profile / Admin", and it showed
   * nothing at all: the strip it named was folded into this menu and the flag was left
   * registered with no component reading it. So ?flag=A turned on a switch wired to no
   * light, and the only way in was to already know the address. It is wired to this now.
   */
  if (!session) {
    return accountBar ? (
      <div className="nav-item">
        <a className="nav-top" href={SCREENS.login}>Sign in</a>
      </div>
    ) : null;
  }

  const isAdmin = session.roles.includes(ADMIN_ROLE);

  return (
    <div className="nav-item">
      <button className="nav-top nav-account" type="button" aria-haspopup="menu">
        <span className="avatar" aria-hidden="true">{initials(session.name || session.email)}</span>
        <span className="sr-only">Account menu for {session.name || session.email}</span>
      </button>

      <div className="nav-panel nav-panel-account" role="menu">
        <div className="account-who">
          <strong>{session.name || session.email}</strong>
          <span>{session.roles.length ? session.roles.join(' · ') : 'No roles'}</span>
        </div>

        <a className="nav-child" href={SCREENS.profile}>Profile</a>
        {isAdmin && <a className="nav-child" href={SCREENS.admin}>Users &amp; registrations</a>}
        {canSeeRequests(session.roles) && <a className="nav-child" href={hrefFor(Page.Requests)}>{w.requestsLabel}</a>}

        {/* Shown to the people who can actually use it. The flag reveals the pencil; it
            grants nothing — the Theme section is WRITE=NONE and the server still
            refuses anyone but an admin. */}
        {isAdmin && (
          <label className="nav-child nav-child-toggle">
            <input
              type="checkbox" checked={themePencil}
              onChange={(e) => setFlag(FLAG_THEME_PENCIL, e.target.checked)}
            />
            Theme editor
          </label>
        )}
        {/* Clearing YOUR browser needs no permission. The server-wide flush is admin only. */}
        <a className="nav-child" href={SCREENS.flushLocal}>Refresh this browser</a>
        {isAdmin && <a className="nav-child" href={SCREENS.flushServer}>Refresh everyone</a>}
        {isAdmin && <a className="nav-child" href={SCREENS.secretFlags}>Feature flags</a>}

        <button className="nav-child nav-child-btn" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}
